import React, { useState, useEffect, useRef, useCallback } from 'react';
import { CartItem } from '../types';
import { fetchVariantBySku, saveMovementBatch, fetchStock, quickCreateVariant } from '../services/db';
import { playScanBeep, playErrorBeep } from '../services/sound';
import { Scanner } from '../components/Scanner';
import { IconCamera, IconTrash, IconQrCode, IconCheck, IconBox } from '../components/Icons';
import { useI18n } from '../i18n';

type SeparationStep = 'SCANNING' | 'ANALYSIS';

export const Separation: React.FC = () => {
  const [step, setStep] = useState<SeparationStep>('SCANNING');
  const [cart, setCart] = useState<CartItem[]>([]);
  const [isScannerOpen, setIsScannerOpen] = useState(false);
  const [manualSku, setManualSku] = useState('');
  const [loading, setLoading] = useState(false);
  const [toast, setToast] = useState<{ msg: string, type: 'success' | 'error' } | null>(null);
  const { t } = useI18n();

  // Analysis State
  const [analyzedStock, setAnalyzedStock] = useState<Record<string, number>>({});
  const [isSuccess, setIsSuccess] = useState(false);

  // Debounce Refs
  const lastScannedRef = useRef<{ sku: string; time: number } | null>(null);
  const DEBOUNCE_MS = 2000;

  // --- SCANNING LOGIC (Similar to Movements) ---

  const handleScan = useCallback(async (sku: string) => {
    const now = Date.now();

    // Check debounce
    if (
      lastScannedRef.current &&
      lastScannedRef.current.sku === sku &&
      now - lastScannedRef.current.time < DEBOUNCE_MS
    ) {
      return;
    }

    lastScannedRef.current = { sku, time: now };

    try {
      // 1. Try to fetch existing
      let variant = await fetchVariantBySku(sku);
      let isNew = false;

      // 2. If not found, Auto-Create (Quick Create Logic)
      if (!variant) {
        try {
          setToast({ msg: t('movements.toast.newItem', { sku }), type: 'success' });
          variant = await quickCreateVariant(sku);
          isNew = true;
        } catch (createErr) {
          playErrorBeep();
          setToast({ msg: t('movements.toast.registerError', { sku }), type: 'error' });
          return;
        }
      }

      playScanBeep();

      setCart(prev => {
        const existingIdx = prev.findIndex(item => item.variant.sku === sku);
        if (existingIdx >= 0) {
          const newCart = [...prev];
          newCart[existingIdx].quantity += 1;
          return newCart;
        } else {
          return [{ variant: variant!, quantity: 1 }, ...prev];
        }
      });

      const actionMsg = isNew
        ? t('movements.toast.newItem', { sku: variant!.sku })
        : t('movements.toast.scanned', { sku: variant!.sku });
      setToast({ msg: actionMsg, type: 'success' });

    } catch (err) {
      playErrorBeep();
      console.error(err);
      setToast({ msg: t('movements.toast.scanError'), type: 'error' });
    }
  }, [t]);

  // Keyboard listener
  useEffect(() => {
    if (step !== 'SCANNING') return; // Only listen in scanning mode

    let buffer = '';
    let timeout: ReturnType<typeof setTimeout>;

    const handleKeyDown = (e: KeyboardEvent) => {
      if ((e.target as HTMLElement).tagName === 'INPUT') return;

      if (e.key === 'Enter') {
        if (buffer.length > 3) {
          handleScan(buffer);
        }
        buffer = '';
      } else if (e.key.length === 1) {
        buffer += e.key;
        clearTimeout(timeout);
        timeout = setTimeout(() => { buffer = ''; }, 100);
      }
    };

    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [handleScan, step]);

  // Cart Management
  const removeFromCart = (sku: string) => {
    setCart(prev => prev.filter(i => i.variant.sku !== sku));
  };

  const updateQuantity = (sku: string, delta: number) => {
    setCart(prev => prev.map(item => {
      if (item.variant.sku === sku) {
        return { ...item, quantity: Math.max(1, item.quantity + delta) };
      }
      return item;
    }));
  };

  const handleManualSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (manualSku) {
      handleScan(manualSku);
      setManualSku('');
    }
  };

  // --- ANALYSIS LOGIC ---

  const handleAnalyze = async () => {
    if (cart.length === 0) return;
    setLoading(true);

    const allStock = await fetchStock();

    const stockMap: Record<string, number> = {};
    allStock.forEach(s => {
      stockMap[s.sku] = s.current_stock;
    });

    setAnalyzedStock(stockMap);
    setStep('ANALYSIS');
    setLoading(false);
  };

  const handleCheckout = async () => {
    setLoading(true);
    try {
      // Filter items that have enough stock
      const validItems = cart.filter(item => {
        const available = analyzedStock[item.variant.sku] || 0;
        return available >= item.quantity;
      });

      if (validItems.length === 0) {
        setToast({ msg: t('toast.availableNone'), type: 'error' });
        setLoading(false);
        return;
      }

      const payload = validItems.map(c => ({ variant_id: c.variant.id, quantity: c.quantity }));
      await saveMovementBatch('OUT', payload, 'Picking / Separation Checkout');

      setIsSuccess(true);
      setToast({ msg: t('toast.checkedOut', { count: validItems.length }), type: 'success' });

      setTimeout(() => {
        setCart([]);
        setStep('SCANNING');
        setIsSuccess(false);
        setAnalyzedStock({});
      }, 2000);

    } catch (e) {
      setToast({ msg: t('movements.toast.saveError'), type: 'error' });
    } finally {
      setLoading(false);
    }
  };

  // Toast Timer
  useEffect(() => {
    if (toast) {
      const t = setTimeout(() => setToast(null), 3000);
      return () => clearTimeout(t);
    }
  }, [toast]);

  // --- RENDER ---

  if (isSuccess) {
    return (
      <div className="flex flex-col items-center justify-center h-full pb-20 animate-in zoom-in-95 duration-300 text-primary">
        <div className="w-20 h-20 bg-success-soft rounded-full flex items-center justify-center mb-4">
          <IconCheck className="w-10 h-10 text-success" />
        </div>
        <h2 className="text-2xl font-bold text-primary">{t('separation.success.title')}</h2>
        <p className="text-muted mt-2 text-center max-w-xs">{t('separation.success.copy')}</p>
        <button
            onClick={() => {
                setIsSuccess(false);
                setStep('SCANNING');
            }}
            className="mt-6 px-6 py-2 bg-accent text-white rounded-lg font-medium"
        >
            {t('separation.success.cta')}
        </button>
      </div>
    );
  }

  return (
    <div className="space-y-4 pb-20 text-primary">
      {/* Header */}
      <div className="bg-surface p-4 rounded-xl shadow-sm border border-subtle sticky top-0 z-10">
        <h2 className="text-lg font-bold text-primary flex items-center gap-2">
           <IconBox className="text-accent" />
           {t('separation.title')}
        </h2>
        {step === 'SCANNING' && (
          <p className="text-xs text-muted">{t('separation.scan.hint')}</p>
        )}
        {step === 'ANALYSIS' && (
          <p className="text-xs text-muted">{t('separation.analysis.hint')}</p>
        )}
      </div>

      {step === 'SCANNING' && (
        <div className="space-y-4 animate-in slide-in-from-left-4 fade-in">
           {/* Scan Controls */}
           <div className="flex gap-2">
              <form onSubmit={handleManualSubmit} className="flex-1 relative">
                <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                    <IconQrCode className="h-5 w-5 text-muted" />
                </div>
                <input
                  type="text"
                  value={manualSku}
                  onChange={(e) => setManualSku(e.target.value.toUpperCase())}
                  placeholder={t('separation.manual.placeholder')}
                  className="w-full pl-10 pr-4 py-3 bg-surface-strong border border-subtle rounded-lg text-sm focus:ring-2 focus:ring-[var(--color-focus)] outline-none"
                />
              </form>
              <button
                onClick={() => setIsScannerOpen(true)}
                className="bg-accent text-white p-3 rounded-lg shadow-md active:scale-95 transition-transform"
              >
                <IconCamera className="w-6 h-6" />
              </button>
           </div>

           {/* List */}
           <div className="space-y-3">
             {cart.length === 0 ? (
                <div className="text-center py-12 text-muted bg-surface rounded-xl border border-dashed border-subtle">
                  <p>{t('separation.empty')}</p>
                </div>
             ) : (
                cart.map((item) => (
                  <div key={item.variant.sku} className="bg-surface p-3 rounded-xl shadow-sm border border-subtle flex items-center justify-between">
                    <div className="flex-1 min-w-0">
                      <div className="font-bold text-primary truncate">{item.variant.sku}</div>
                      <div className="text-xs text-muted">{item.variant.productName}</div>
                    </div>
                    <div className="flex items-center gap-3 pl-3">
                      <div className="flex items-center bg-surface-strong rounded-lg border border-subtle">
                        <button onClick={() => updateQuantity(item.variant.sku, -1)} className="w-8 h-8 flex items-center justify-center text-primary hover:bg-surface rounded-l-lg">-</button>
                        <span className="w-8 text-center text-sm font-semibold">{item.quantity}</span>
                        <button onClick={() => updateQuantity(item.variant.sku, 1)} className="w-8 h-8 flex items-center justify-center text-primary hover:bg-surface rounded-r-lg">+</button>
                      </div>
                      <button onClick={() => removeFromCart(item.variant.sku)} className="text-muted hover:text-danger p-1">
                        <IconTrash className="w-5 h-5" />
                      </button>
                    </div>
                  </div>
                ))
             )}
           </div>

           {/* Action Button */}
           {cart.length > 0 && (
             <div className="fixed bottom-0 left-0 right-0 px-4 pt-4 pb-[calc(env(safe-area-inset-bottom)+5rem)] bg-surface border-t border-subtle z-60 md:static md:bg-transparent md:border-0 md:p-0 md:pb-0 md:pt-0 md:px-0">
                <button
                  onClick={handleAnalyze}
                  disabled={loading}
                  className="w-full py-4 bg-accent text-white rounded-xl font-bold shadow-lg hover:brightness-110 disabled:opacity-50"
                >
                  {loading ? t('separation.analyzing') : t('separation.analyze', { count: cart.reduce((a,b)=>a+b.quantity,0) })}
                </button>
             </div>
           )}
        </div>
      )}

      {step === 'ANALYSIS' && (
        <div className="space-y-4 animate-in slide-in-from-right-4 fade-in">
           <div className="bg-surface rounded-xl shadow-sm border border-subtle overflow-hidden">
             <div className="p-3 bg-surface-strong border-b border-subtle flex justify-between items-center">
               <span className="text-xs font-bold text-muted uppercase">{t('separation.report.title')}</span>
               <button onClick={() => setStep('SCANNING')} className="text-xs text-accent font-medium hover:underline">
                 {t('separation.report.edit')}
               </button>
             </div>

             <div className="divide-y divide-[var(--color-border)]">
               {cart.map(item => {
                 const available = analyzedStock[item.variant.sku] || 0;
                 const isEnough = available >= item.quantity;

                 return (
                   <div key={item.variant.sku} className="p-4 flex items-center justify-between">
                     <div className="flex-1">
                        <div className="font-bold text-sm text-primary">{item.variant.sku}</div>
                        <div className="flex items-center gap-2 mt-1 text-muted">
                          <span className="text-xs">{t('separation.report.requested')}: <b>{item.quantity}</b></span>
                          <span className="text-muted">|</span>
                          <span className="text-xs">{t('separation.report.inStock')}: <b>{available}</b></span>
                        </div>
                     </div>
                     <div className={`px-3 py-1 rounded-full text-xs font-bold border ${
                       isEnough
                        ? 'bg-success-soft text-success border-success'
                        : 'bg-danger-soft text-danger border-danger'
                     }`}>
                        {isEnough ? t('separation.report.available') : t('separation.report.missing')}
                     </div>
                   </div>
                 );
               })}
             </div>
           </div>

           <div className="fixed bottom-0 left-0 right-0 px-4 pt-4 pb-[calc(env(safe-area-inset-bottom)+5rem)] bg-surface border-t border-subtle z-60 md:static md:bg-transparent md:border-0 md:p-0 md:pb-0 md:pt-0 md:px-0">
              <button
                onClick={handleCheckout}
                disabled={loading}
                className="w-full py-4 bg-accent text-white rounded-xl font-bold shadow-lg hover:bg-accent disabled:opacity-50"
              >
                {loading ? t('separation.processing') : t('separation.confirm')}
              </button>
              <p className="text-center text-[10px] text-muted mt-2">
                {t('separation.notice')}
              </p>
           </div>
        </div>
      )}

      {/* Toast */}
      {toast && (
        <div className={`fixed top-4 left-1/2 -translate-x-1/2 px-4 py-2 rounded-full text-sm font-medium shadow-xl z-50 animate-in fade-in slide-in-from-top-2 ${
          toast.type === 'success' ? 'bg-accent text-white' : 'bg-danger text-white'
        }`}>
          {toast.msg}
        </div>
      )}

      <Scanner
        isActive={isScannerOpen}
        onScan={handleScan}
        onClose={() => setIsScannerOpen(false)}
      />
    </div>
  );
};
